import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveMoveGifUrl } from '../../utils/moveGifUrl';
import { MOVE_GIF_MAP } from '../../utils/moveGifMap';
import './character-switch.css';
import './SimpleMoveAnimation.css';
import './switch-animation.css';

// Longer dwell time so newcomers can read the title, GIF, and damage summary.
// (~4s per action in a two-hit turn ≈ earlier pacing before the snappy 1.7s experiment.)
const MOVE_DISPLAY_MS = 4000;
const FINAL_DELAY_MS = 400;

const SimpleMoveAnimation = ({
  battleAnimationData,
  onAnimationComplete,
  viewerSocketId,
  yourTrainerName = 'You',
  opponentTrainerName = 'Opponent'
}) => {
  const [isActive, setIsActive] = useState(true);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [gifLoaded, setGifLoaded] = useState(false);

  // Preload all gifs in the sequence so the second move doesn't flash unloaded
  useEffect(() => {
    if (!battleAnimationData || !battleAnimationData.actions) return;
    battleAnimationData.actions.forEach(action => {
      const move = action?.msg?.move;
      if (!move?.moveId) return;
      const url = resolveMoveGifUrl(move, MOVE_GIF_MAP);
      const preload = new Image();
      preload.src = url;
    });
  }, [battleAnimationData]);

  // Reset gifLoaded flag when move index changes so title/gif stay in sync
  useEffect(() => {
    setGifLoaded(false);
  }, [currentMoveIndex]);

  // Handle the animation sequence
  useEffect(() => {
    console.log("SimpleMoveAnimation received data:", battleAnimationData);

    if (!battleAnimationData || !battleAnimationData.actions || battleAnimationData.actions.length === 0) {
      console.log("No valid battle animation data, skipping animation");
      if (onAnimationComplete) {
        onAnimationComplete();
      }
      return;
    }

    console.log("Processing animation sequence with", battleAnimationData.actions.length, "actions");
    const totalMoves = battleAnimationData.actions.length;

    const moveTimeout = setTimeout(() => {
      if (currentMoveIndex < totalMoves - 1) {
        setCurrentMoveIndex(prevIndex => prevIndex + 1);
      } else {
        const finalTimeout = setTimeout(() => {
          setIsActive(false);
          if (onAnimationComplete) {
            onAnimationComplete();
          }
        }, FINAL_DELAY_MS);

        return () => clearTimeout(finalTimeout);
      }
    }, MOVE_DISPLAY_MS);

    return () => clearTimeout(moveTimeout);
  }, [onAnimationComplete, battleAnimationData, currentMoveIndex]);

  // Skip to the end handler
  const handleSkip = () => {
    setIsActive(false);
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  };

  if (!isActive || !battleAnimationData || !battleAnimationData.actions || battleAnimationData.actions.length === 0) {
    console.log("SimpleMoveAnimation returning null due to:", {
      isActive,
      hasAnimationData: !!battleAnimationData,
      hasActions: battleAnimationData ? !!battleAnimationData.actions : false,
      actionsLength: battleAnimationData && battleAnimationData.actions ? battleAnimationData.actions.length : 0
    });
    return null;
  }

  // Get the current action to display
  const currentAction = battleAnimationData.actions[currentMoveIndex];
  console.log("Rendering move at index", currentMoveIndex, ":", currentAction);

  if (!currentAction || !currentAction.msg) {
    console.error("Invalid action data at index", currentMoveIndex);
    return null;
  }

  const moveAction = currentAction.msg;

  // Check if this is a switch action
  const isSwitch = currentAction.isSwitch === true;

  // Get characters from the action
  const attacker = moveAction.userSelectedCharecter;
  const defender = moveAction.opponentSelectedCharecter;
  const move = moveAction.move;
  const switchTo = moveAction.switchTo;

  const moveIsMine =
    Boolean(viewerSocketId) && currentAction.socketId === viewerSocketId;
  const atkTrainer = moveIsMine ? yourTrainerName : opponentTrainerName;
  const defTrainer = moveIsMine ? opponentTrainerName : yourTrainerName;

  // Handle switch actions with Pokémon-style animation
  if (isSwitch) {
    const switchCharacter = switchTo || attacker;

    if (!switchCharacter) {
      console.error("Missing switch character data");
      setTimeout(() => {
        setIsActive(false);
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, 0);
      return null;
    }

    return createPortal(
      <div className="move-animation-overlay active">
        <div className="move-animation-container">
          <div className="move-sequence-indicator">
            Character Switch
            <button className="move-skip-button" onClick={handleSkip}>Skip</button>
          </div>
          <div className="move-trainer-context" aria-hidden="true">
            <span className="move-trainer-pill move-trainer-pill--attacker">{atkTrainer}</span>
            <span className="move-trainer-vs">·</span>
            <span className="move-trainer-pill move-trainer-pill--defender">{defTrainer}</span>
          </div>
          <div className="move-animation-item">
            <div className="move-animation-title switch-title">
              {atkTrainer}: {switchCharacter.name} was sent out!
            </div>
            <div className="move-animation-gif-container">
              <div className="switch-animation">
                <div className="switch-text">
                  Go! {switchCharacter.name}!
                  <span className="switch-character-name"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Handle null or undefined values for regular moves
  if (!attacker || !defender || !move) {
    console.error("Missing essential character or move data:", { attacker, defender, move });

    // For other cases, complete the animation
    setTimeout(() => {
      setIsActive(false);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }, 0);
    return null;
  }

  console.log("Move data:", {
    attacker: attacker?.name,
    attackerHealth: attacker?.modifiedHealth,
    defender: defender?.name,
    defenderHealth: defender?.modifiedHealth,
    move: move?.name,
    movePower: move?.power,
    isSwitch: isSwitch
  });

  // Check if this move was missed
  const isMissed = currentAction.isMissed === true;

  console.log("Move missed check:", {
    moveId: move?.moveId,
    missedMoves: battleAnimationData.missedMoves,
    isMissed: isMissed
  });

  // Determine the move effects (stat labels reference whose character changed)
  const getEffectsText = () => {
    if (!move || !move.effects || move.effects.length === 0) return [];

    return move.effects
      .filter(effect => effect && effect.type && (effect.type.startsWith('user_') || effect.type.startsWith('opponent_')))
      .map(effect => {
        const effectType = effect.change === 'increase' ? 'increased' : 'decreased';
        const fieldLabel = (effect.field || '').replace(/_/g, ' ');
        if (effect.type.startsWith('user_')) {
          const who = moveIsMine ? 'Your' : `${opponentTrainerName}'s`;
          return `${who} ${attacker.name}'s ${fieldLabel} ${effectType}!`;
        }
        const who = moveIsMine ? `${opponentTrainerName}'s` : 'Your';
        return `${who} ${defender.name}'s ${fieldLabel} ${effectType}!`;
      });
  };

  // Determine if a character has died
  const hasDied = defender && defender.modifiedHealth <= 0;

  /** Server sends `damage` on the move event (points of HP for this hit). */
  const rawDmg = currentAction.damage;
  const damageThisMove =
    typeof rawDmg === 'number' && !Number.isNaN(rawDmg)
      ? rawDmg
      : rawDmg != null && !Number.isNaN(Number(rawDmg))
        ? Number(rawDmg)
        : null;

  const getDamageThisMoveLine = () => {
    if (isMissed) return null;
    if (!move || !move.power || move.power === 0) return null;
    if (damageThisMove == null) return null;
    if (damageThisMove <= 0) return '0';
    return String(damageThisMove);
  };

  const getRemainingHpLine = () => {
    if (!defender) return null;
    const maxHp = Number(defender.health);
    const cur = Number(defender.modifiedHealth);
    if (!Number.isFinite(maxHp) || maxHp <= 0) {
      return Number.isFinite(cur) ? `Defender HP: ${cur}` : null;
    }
    const safeCur = Number.isFinite(cur) ? Math.max(0, cur) : 0;
    return `Defender HP: ${safeCur} / ${maxHp}`;
  };

  return createPortal(
    <div className="move-animation-overlay active">
      <div className="move-animation-container">
        <div className="move-sequence-indicator">
          Move {currentMoveIndex + 1} of {battleAnimationData.actions.length}
          <button className="move-skip-button" onClick={handleSkip}>Skip</button>
        </div>

        <div className="move-trainer-context" aria-hidden="true">
          <span className="move-trainer-pill move-trainer-pill--attacker">{atkTrainer} · Attacks</span>
          <span className="move-trainer-vs">→</span>
          <span className="move-trainer-pill move-trainer-pill--defender">{defTrainer} · Defends</span>
        </div>

        <div className="move-animation-item">
          <>
            <div className="move-animation-title">
              {attacker?.name} used {move?.name}!
            </div>
            {isMissed && (
              <div className="move-missed-message">
                {defender?.name} avoided the hit!
              </div>
            )}
          </>

          {/* Show death message if character has died */}
          {hasDied && defender && (
            <div className="character-death-message">
              <div className="death-icon">☠️</div>
              <div className="death-text">
                {defender.name} has been defeated!
                <div className="death-instruction">Select another character to continue</div>
              </div>
            </div>
          )}

          {/* Display switch animation or regular move animation */}
          {(!hasDied || !defender) && (
            <div className="move-animation-gif-container">
              {isMissed ? (
                // Show miss animation instead of move GIF
                <div className="move-animation-gif miss-animation" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: '#ef4444',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '40px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px dashed #ef4444',
                  borderRadius: '8px'
                }}>
                  MISS!
                </div>
              ) : (
                // Regular move animation
                (() => {
                  // Make sure move exists
                  if (!move || !move.moveId) {
                    return (
                      <div className="move-animation-gif fallback">
                        Move animation unavailable
                      </div>
                    );
                  }

                  // Convert move name to moveId format
                  if (!move || !move.moveId) {
                    return (
                      <div className="move-animation-gif fallback">
                        Move animation unavailable
                      </div>
                    );
                  }

                  // Convert move name to moveId format
                  const gifUrl = resolveMoveGifUrl(move, MOVE_GIF_MAP);

                  console.log("Loading move GIF:", gifUrl);

                  return (
                    <div className={`move-animation-gif ${gifLoaded ? 'loaded' : 'loading'}`}>
                      {!gifLoaded && (
                        <div className="gif-loading-placeholder">
                          <div className="gif-loading-spinner"></div>
                        </div>
                      )}
                      <img
                        src={gifUrl}
                        alt={move.name}
                        loading="eager"
                        onLoad={() => setGifLoaded(true)}
                        onError={(e) => {
                          e.target.onerror = null;
                          console.error("Failed to load GIF:", gifUrl);
                          setGifLoaded(true);

                          const container = e.target.closest('.move-animation-gif');
                          if (container) {
                            container.classList.add('fallback');
                            container.innerHTML = `${move.name} animation`;
                          } else {
                            e.target.src = "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100%25' height='100%25' fill='%23444'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='monospace' font-size='14px' fill='white'%3EMove animation%3C/text%3E%3C/svg%3E";
                          }
                        }}
                      />
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {move && move.power > 0 && defender && !isMissed && (() => {
            const damageValue = getDamageThisMoveLine();
            const hpLine = getRemainingHpLine();
            if (!damageValue && !hpLine) return null;
            return (
            <div className={`move-damage-summary ${hasDied ? 'fatal' : ''}`}>
              {damageValue != null && (
                <div className="damage-dealt">
                  <span className="damage-dealt-label">Damage</span>
                  <span className="damage-dealt-value">
                    {damageValue === '0' ? '0' : `−${damageValue}`}
                  </span>
                </div>
              )}
              {hpLine && (
                <div className="hp-remaining">{hpLine}</div>
              )}
            </div>
            );
          })()}

          {!isMissed && getEffectsText().map((effect, i) => (
            <div key={i} className="move-effect-change">
              {effect}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SimpleMoveAnimation;
